import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert } from 'reactstrap';
import axios from '../store/axios';

const AdminNotificationPopup = () => {
  const [notifications, setNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();
  
  // Get current workspace and user from Redux store
  const authState = useSelector(state => state.Auth);
  const currentWorkspace = authState?.user?.currentWorkspace || authState?.currentWorkspace;
  const user = authState?.user;
  const userRole = authState?.user?.role;
  const memberRole = authState?.memberRole;
  
  // Debug logging
  console.log('Auth State:', authState);
  console.log('Current Workspace:', currentWorkspace);
  console.log('User:', user);

  useEffect(() => {
    // Only fetch notifications if user is logged in and has a workspace
    if (currentWorkspace && user) {
      console.log('Starting notification fetch for workspace:', currentWorkspace);
      console.log('User role:', userRole, 'Member role:', memberRole);
      
      // Don't fetch for system admins unless they're also workspace members
      if (userRole === 'admin' && !memberRole) {
        console.log('Skipping notifications for system admin');
        return;
      }
      
      fetchNotifications();
      // Check for new notifications every 15 seconds for faster updates
      const interval = setInterval(fetchNotifications, 15000);
      return () => clearInterval(interval);
    } else {
      console.log('Not fetching notifications - Missing workspace or user');
      console.log('Workspace:', currentWorkspace, 'User:', user);
    }
  }, [currentWorkspace, user, userRole, memberRole]);

  const fetchNotifications = async () => {
    try {
      console.log('Checking for notifications for workspace:', currentWorkspace);
      const response = await axios.get('/user/workspace/notifications');
      console.log('Full notifications response:', response);
      console.log('Response data:', response.data);
      console.log('Response body:', response.data?.body);
      
      // The response structure is { code, message, body: { notifications: [...] } }
      const allNotifications = response.data?.body?.notifications || [];
      console.log('All notifications:', allNotifications);
      
      const unreadNotifications = allNotifications.filter(n => !n.read);
      console.log('Unread notifications count:', unreadNotifications.length);
      
      if (unreadNotifications.length > 0) {
        console.log('First unread notification:', unreadNotifications[0]);
        setNotifications(unreadNotifications);
        // Always update to show the latest unread notification
        setCurrentNotification(unreadNotifications[0]);
        setShowModal(true);
        console.log('Modal should be showing for:', unreadNotifications[0].subject);
      } else {
        console.log('No unread notifications found');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error.response || error);
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
      }
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`/user/workspace/notifications/${notificationId}/read`);
      
      // Remove from notifications list
      const updatedNotifications = notifications.filter(n => n._id !== notificationId);
      setNotifications(updatedNotifications);
      
      // Show next notification if available
      if (updatedNotifications.length > 0) {
        setCurrentNotification(updatedNotifications[0]);
        setShowModal(true);
      } else {
        setCurrentNotification(null);
        setShowModal(false);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleClose = () => {
    if (currentNotification) {
      markAsRead(currentNotification._id);
    }
  };

  // Debug logging
  console.log('Render check - Current notification:', currentNotification);
  console.log('Render check - Show modal:', showModal);
  console.log('Render check - User role:', userRole);
  console.log('Render check - Member role:', memberRole);
  
  // Don't render if no notification or user is a system admin
  // System admins have role === 'admin', workspace members have memberRole
  if (!currentNotification || !showModal) {
    console.log('Not rendering: No notification or modal not shown');
    return null;
  }
  
  // Check if user is system admin (they shouldn't see workspace notifications)
  if (userRole === 'admin' && !memberRole) {
    console.log('Not rendering: User is system admin');
    return null;
  }

  return (
    <>
      {/* Overlay to ensure notification stays on top */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9998,
        display: showModal ? 'block' : 'none'
      }} />
      
      <Modal 
        isOpen={showModal} 
        toggle={() => {}} // Disable toggle on backdrop click
        size="md"
        className="admin-notification-modal"
        backdrop="static"
        keyboard={false}
        style={{ zIndex: 10000 }}
        centered
      >
        <ModalHeader className="bg-primary text-white" style={{ border: 'none' }}>
          <i className="fa fa-bell mr-2"></i>
          {currentNotification.subject || 'System Notification'}
          <button 
            type="button" 
            className="close text-white ml-auto" 
            onClick={handleClose}
            style={{ opacity: 1 }}
          >
            <span>&times;</span>
          </button>
        </ModalHeader>
      <ModalBody>
        <Alert color="info" className="mb-3">
          <small className="text-muted">
            From: {currentNotification.from || 'System Administrator'}
            <br />
            Date: {new Date(currentNotification.createdAt).toLocaleString()}
          </small>
        </Alert>
        <div className="notification-message" style={{ 
          padding: '15px',
          backgroundColor: 'var(--sa-dark-surface-light)',
          borderRadius: '5px',
          minHeight: '100px'
        }}>
          <p style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
            {currentNotification.message}
          </p>
        </div>
        {notifications.length > 1 && (
          <Alert color="warning" className="mt-3 mb-0">
            <small>You have {notifications.length - 1} more unread notification(s)</small>
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleClose}>
          <i className="fa fa-check mr-2"></i>
          Mark as Read & Close
        </Button>
      </ModalFooter>
    </Modal>
    
    {/* Additional styles to ensure notification stays on top */}
    <style jsx>{`
      .admin-notification-modal {
        position: fixed !important;
        z-index: 10000 !important;
      }
      .modal-backdrop {
        z-index: 9999 !important;
      }
    `}</style>
    </>
  );
};

export default AdminNotificationPopup;